import type { CompanyProfile, Invoice } from "@/api/types";
import { formatAmount } from "@/lib/money";
import { PrintAmountInWords, PrintFooter, PrintHeader, PrintSignature } from "./DocumentSheet";
import { usePrintBranding } from "./branding";
import { useTaxWords } from "@/app/session";
import { useInvoiceSetting } from "@/api/queries";
import { termsLabel } from "@/lib/paymentTerms";

/**
 * An invoice as printed from the books. It is not an IRD-approved tax invoice (ADR-0027).
 *
 * The totals block is the EN 16931 chain in the order `lib/invoiceTotals.ts` computes it, so what
 * is printed is what was calculated rather than a second arithmetic on the way to paper. A printed
 * total that disagrees with the document is the one error nobody forgives.
 */
export function TaxInvoicePrint({
  invoice,
  seller,
}: {
  invoice: Invoice;
  seller: CompanyProfile | undefined;
}) {
  const say = useTaxWords();
  const branding = usePrintBranding("invoice");
  const { data: setting } = useInvoiceSetting();
  const showDiscount =
    branding.showDiscount && invoice.lines.some((line) => Number(line.allowances ?? 0) > 0);
  const showHsCode = (setting?.showHsCode ?? false) && invoice.lines.some((line) => line.itemCode);
  const terms =
    invoice.paymentTermsDays === undefined
      ? null
      : `Payment terms: ${termsLabel(invoice.paymentTermsDays)}`;
  return (
    <article>
      <PrintHeader
        seller={seller}
        documentTitle={invoice.status === "draft" ? "Draft invoice" : "Invoice"}
        number={invoice.number}
        date={invoice.date}
        buyerLabel="Billed to"
        buyerName={invoice.customerName}
        buyerTaxId={invoice.customerTaxId}
        buyerRegistration={invoice.customerLegalRegistration}
      />

      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What was invoiced</caption>
        <thead>
          <tr className="border-border border-b">
            <th scope="col" className="text-muted-foreground py-1.5 text-left text-xs font-medium">
              Description
            </th>
            {showHsCode ? (
              <th
                scope="col"
                className="text-muted-foreground py-1.5 text-left text-xs font-medium"
              >
                HS code
              </th>
            ) : null}
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Qty
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Unit price
            </th>
            {showDiscount ? (
              <th
                scope="col"
                className="text-muted-foreground py-1.5 text-right text-xs font-medium"
              >
                Discount
              </th>
            ) : null}
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              {say("VAT")}
            </th>
            <th scope="col" className="text-muted-foreground py-1.5 text-right text-xs font-medium">
              Amount
            </th>
          </tr>
        </thead>
        <tbody>
          {invoice.lines.map((line) => (
            <tr key={line.n} className="border-muted border-b">
              <td className="text-foreground py-1.5">{line.itemName}</td>
              {showHsCode ? (
                <td className="num text-muted-foreground py-1.5">{line.itemCode ?? ""}</td>
              ) : null}
              <td className="num text-muted-foreground py-1.5 text-right">{line.quantity}</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.unitPrice)}
              </td>
              {showDiscount ? (
                <td className="num text-muted-foreground py-1.5 text-right">
                  {Number(line.allowances ?? 0) > 0 ? formatAmount(line.allowances!) : ""}
                </td>
              ) : null}
              <td className="num text-muted-foreground py-1.5 text-right">{line.vatRatePct}%</td>
              <td className="num text-foreground py-1.5 text-right">
                {formatAmount(line.lineNet)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="ml-auto mt-4 w-64 text-sm">
        <Figure label="Net" value={invoice.totals.taxExclusive} />
        {Number(invoice.totals.allowances) !== 0 ? (
          <Figure label="Less allowances" value={invoice.totals.allowances} />
        ) : null}
        {Number(invoice.totals.charges) !== 0 ? (
          <Figure label="Charges" value={invoice.totals.charges} />
        ) : null}
        <Figure label={say("VAT")} value={invoice.totals.vat} />
        <Figure label="Total" value={invoice.totals.taxInclusive} emphasis />
        {Number(invoice.totals.paid) !== 0 ? (
          <>
            <Figure label="Paid" value={invoice.totals.paid} />
            <Figure label="Due" value={invoice.totals.due} emphasis />
          </>
        ) : null}
      </dl>

      {/* Someone who has already paid at the counter isn't being asked how to pay. */}
      <PrintAmountInWords amount={invoice.totals.taxInclusive} currency={invoice.currency} />

      {branding.showPaymentDetails && invoice.paymentDetails && !invoice.cashSale ? (
        <section className="mt-4 text-sm">
          <h2 className="font-medium">How to pay</h2>
          <p className="text-muted-foreground whitespace-pre-line">{invoice.paymentDetails}</p>
        </section>
      ) : null}

      <PrintSignature byLabel="Prepared by" />

      <PrintFooter
        note={
          invoice.status === "draft" ? (
            "This is a draft. It has no number and has not been entered in the books."
          ) : terms || invoice.footerText ? (
            <>
              {terms}
              {terms && invoice.footerText ? <br /> : null}
              {invoice.footerText}
            </>
          ) : undefined
        }
      />
    </article>
  );
}

function Figure({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div
      className={[
        "flex items-baseline justify-between py-1",
        emphasis ? "border-border text-foreground border-t font-medium" : "text-muted-foreground",
      ].join(" ")}
    >
      <dt>{label}</dt>
      <dd className="num text-foreground">{formatAmount(value)}</dd>
    </div>
  );
}
